import { inject, Injectable } from "@angular/core";
import { defer, forkJoin, iif, lastValueFrom, of, switchMap } from "rxjs";
import { ReminderBackend } from "src/app/backend/api/reminder.backend";
import { Reminder } from "src/app/backend/models/reminder";
import { hubListen } from "src/app/core/hub/hub";
import { HubChannels } from "src/app/core/hub/hub-channels";
import { HubEvent } from "src/app/core/hub/hub-events";
import { NotifyAppointmentFunctionService } from "src/app/modules/bootstrap/pms-ui/functions/appointment/notify-appointment/notify-appointment-function.service";

export type AppointmentUpdatedEvent = {
  id: string;
};

export type AppointmentCreatedEvent = {
  id: string;
};

export type AppointmentRescheduledEvent = {
  id: string;
};

export type AppointmentCanceledEvent = {
  id: string;
};

export type AppointmentDeletedEvent = {
  id: string;
};

type AppointmentEvent =
  | AppointmentUpdatedEvent
  | AppointmentCanceledEvent
  | AppointmentCreatedEvent
  | AppointmentRescheduledEvent
  | AppointmentDeletedEvent;

@Injectable()
export class AppointmentEventsService {
  private readonly reminderBackend = inject(ReminderBackend);

  private readonly notifyAppointmentFunctionService = inject(
    NotifyAppointmentFunctionService
  );

  register(): void {
    hubListen(
      HubChannels.EVENTS,
      async (capsule: {
        channel: string;
        payload: { event: HubEvent; data?: AppointmentEvent };
      }) => {
        switch (capsule.payload.event) {
          case HubEvent.APPOINTMENT_UPDATED:
          case HubEvent.APPOINTMENT_RESCHEDULED:
          case HubEvent.APPOINTMENT_CREATED: {
            await this.notifyAppointmentFunctionService.execute({
              appointmentId: capsule.payload.data!.id
            });

            break;
          }
          case HubEvent.APPOINTMENT_DELETED:
          case HubEvent.APPOINTMENT_CANCELED: {
            lastValueFrom(
              iif(
                () => !!capsule.payload.data,
                defer(() => {
                  return this.reminderBackend
                    .listByResource(capsule.payload.data!.id)
                    .pipe(
                      switchMap((reminders: Reminder[]) => {
                        return iif(
                          () => !!reminders && reminders.length > 0,
                          defer(() => {
                            return forkJoin(
                              reminders.map((reminder) => {
                                return this.reminderBackend.deleteSilently(
                                  reminder.id
                                );
                              })
                            );
                          }),
                          of(undefined)
                        );
                      })
                    );
                }),
                of(undefined)
              )
            );
            break;
          }
        }
      }
    );
  }
}
